import React from 'react';
import PropTypes from 'prop-types';
import Toggle from '@mulesoft/anypoint-components/lib/Toggle';
import styles from '../ScannerConfigurationForm.css';

/**
 * Configuration object for status-based switch behavior
 * Maps each status to its visual and text properties
 */
const STATUS_CONFIG = {
  SCHEDULED: {
    checked: true,
    color: 'blue', // Primary color
    helperText: null,
    helperTextColor: null,
    trackClass: null // No special styling
  },
  PAUSED: {
    checked: false,
    color: 'gray', // Default/Neutral
    helperText:
      'Scheduled scan is paused. Enable the schedule to resume updating agent data.',
    helperTextColor: 'var(--lume-g-color-neutral-60)',
    trackClass: null
  },
  STOPPED: {
    checked: false,
    color: 'red', // Error indicator
    helperText:
      'Scheduled scan disabled. Fix the credentials to reactivate the scan schedule.',
    helperTextColor: 'var(--lume-g-color-red-50)',
    trackClass: 'toggle-track-error' // Custom red track styling
  }
};

/**
 * ScheduledScanSwitch Component
 *
 * A controlled toggle switch component that displays different states for scheduled scans.
 * Provides visual feedback and helper messages based on the current status.
 *
 * @param {Object} props - Component props
 * @param {string} props.status - Current status: 'SCHEDULED', 'PAUSED', or 'STOPPED'
 * @param {Function} props.onToggle - Callback function when toggle is clicked
 */
const ScheduledScanSwitch = ({ status, onToggle }) => {
  // Get configuration for current status
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.PAUSED;

  return (
    <React.Fragment>
      <Toggle
        id="scheduled-scan-toggle"
        testId="scheduled-scan-toggle"
        checked={config.checked}
        onChange={onToggle}
        className={
          status === 'STOPPED'
            ? styles['toggle-error']
            : styles['toggle-default']
        }
      />

      {/* Conditional helper text */}
      {config.helperText && (
        <div
          className={styles['switch-helper-text']}
          style={{ color: config.helperTextColor }}
        >
          {config.helperText}
        </div>
      )}
    </React.Fragment>
  );
};

ScheduledScanSwitch.propTypes = {
  /** Current status of the scheduled scan */
  status: PropTypes.oneOf(['SCHEDULED', 'PAUSED', 'STOPPED']).isRequired,
  /** Callback when the toggle switch is clicked */
  onToggle: PropTypes.func.isRequired
};

export default ScheduledScanSwitch;
